import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building, Briefcase, Clock, Quote } from "lucide-react";
import { findProject, PROJECTS } from "../lib/portfolioData";
import SEOHead from "../components/SEOHead";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const p = findProject(slug);

  if (!p) {
    return (
      <div className="section text-center">
        <h1 className="text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Case study not found</h1>
        <Link to="/portfolio" className="btn-primary mt-6 inline-flex">Back to portfolio</Link>
      </div>
    );
  }

  const related = PROJECTS.filter(x => x.slug !== p.slug).slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": p.title,
    "description": p.desc,
    "image": p.image,
    "author": {"@type": "Organization", "name": "Optivize Digital"},
    "about": p.category,
  };

  return (
    <div>
      <SEOHead title={`${p.title} — Case Study · Optivize Digital`} description={p.desc} image={p.image} schema={schema}/>

      {/* Hero */}
      <section className="relative">
        <div className="aspect-[16/7] sm:aspect-[16/6] lg:aspect-[16/5] w-full overflow-hidden relative">
          <img src={p.image} alt={p.title} className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/20" />
        </div>
        <div className="max-w-5xl mx-auto px-5 -mt-40 sm:-mt-44 relative z-10">
          <Link to="/portfolio" className="inline-flex items-center gap-1.5 text-sm text-blue-300 hover:text-blue-200 mb-4" data-testid="back-to-portfolio">
            <ArrowLeft size={14}/> All case studies
          </Link>
          <span className="tag !bg-blue-600 !text-white !border-blue-600 mb-4">{p.tag}</span>
          <motion.h1
            initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:.5}}
            className="text-4xl sm:text-6xl font-extrabold text-white"
            style={{fontFamily:'Outfit'}}
            data-testid="case-title"
          >
            {p.title}
          </motion.h1>
          <p className="text-slate-300 mt-4 max-w-3xl text-lg">{p.desc}</p>
        </div>
      </section>

      {/* Project meta */}
      <section className="py-10 px-5">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-4">
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><Building size={12}/> Client</div>
            <div className="text-white font-semibold">{p.client}</div>
            <div className="text-slate-400 text-sm">{p.industry}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><Briefcase size={12}/> Services</div>
            <div className="text-white text-sm leading-relaxed">{p.services.join(" · ")}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><Clock size={12}/> Timeline</div>
            <div className="text-white font-semibold">{p.duration}</div>
            <div className="text-emerald-400 text-sm">{p.impact}</div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 px-5" style={{background:'var(--bg-section)'}}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-8" style={{fontFamily:'Outfit'}}>Results</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {p.results.map((r, i) => (
              <div key={i} className="card p-6 text-center">
                <div className="text-3xl sm:text-4xl font-extrabold text-blue-400" style={{fontFamily:'Outfit'}}>{r.metric}</div>
                <div className="text-slate-400 mt-1 text-sm">{r.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge + Solution */}
      <section className="py-12 px-5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="card p-7">
            <div className="text-blue-400 text-xs uppercase tracking-[0.18em] font-semibold mb-3">The challenge</div>
            <p className="text-slate-200 leading-relaxed">{p.challenge}</p>
          </div>
          <div className="card p-7">
            <div className="text-blue-400 text-xs uppercase tracking-[0.18em] font-semibold mb-3">Our solution</div>
            <p className="text-slate-200 leading-relaxed">{p.solution}</p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {p.gallery?.length > 0 && (
        <section className="py-12 px-5" style={{background:'var(--bg-section)'}}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6" style={{fontFamily:'Outfit'}}>Gallery</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {p.gallery.map((src, i) => (
                <div key={i} className="card overflow-hidden">
                  <img src={src} alt={`${p.title} ${i+1}`} className="w-full h-full object-cover aspect-[3/2]" loading="lazy"/>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      <section className="py-16 px-5">
        <div className="max-w-3xl mx-auto card p-8 sm:p-10">
          <Quote className="text-blue-400 mb-4" size={32}/>
          <blockquote className="text-2xl sm:text-3xl text-white leading-relaxed" style={{fontFamily:'Outfit'}}>
            "{p.testimonial.quote}"
          </blockquote>
          <div className="mt-6 text-slate-400 text-sm">
            <span className="text-white font-semibold">{p.testimonial.name}</span> · {p.testimonial.role}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-5" style={{background:'var(--bg-section)'}}>
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            Want results like these?
          </h3>
          <p className="text-slate-400 mt-3">Tell us about your project and we'll send a tailored proposal within 24 hours.</p>
          <Link to="/contact" className="btn-primary mt-6 inline-flex">Start your project <ArrowRight size={16}/></Link>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-6" style={{fontFamily:'Outfit'}}>More case studies</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/portfolio/${r.slug}`} className="card overflow-hidden group block">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy"/>
                </div>
                <div className="p-5">
                  <span className="text-xs uppercase tracking-wider text-blue-400 font-semibold">{r.category}</span>
                  <h3 className="text-white font-bold mt-2" style={{fontFamily:'Outfit'}}>{r.title}</h3>
                  <div className="mt-3 text-blue-400 text-sm inline-flex items-center gap-1">View case study <ArrowRight size={14}/></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
